import { Typography, useTheme } from "@material-ui/core/";
import { GreenSwitch, IconButton } from '@coolremote/shared';
import { AccessTime } from "@material-ui/icons/";
import clsx from "clsx";
import { isEmpty, isNumber } from "lodash";
import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { t } from "ttag";
import { symbols } from "../../../../constants/sensorsUnitsSymbols";
import Tooltip from "../../../../components/Tooltip/LightTooltip";
import { AlertControl, ArrowDownO, Disconnect as DisconnectIcon } from "../../../../icons";
import { useStoreActions, useStoreState } from "../../../../models/RootStore";
import { useGetBacnetParamTemplate } from "@hooks/useBacnetParamTemplatesApi";
import { useGetBacnetUiTemplate } from "@hooks/useBacnetUiTemplatesApi";
import { useSetBacnetObjectValue } from "@hooks/useBacnetDevicesApi";
import {
  AutoMode,
  AutoVRMode,
  ColdMode,
  DryIcon,
  HotMode,
  ModeFanIcon,
  Timer,
  TimerActive
} from "../../../../svgComponents";
import useStyle from "./ControlItem.style";
import RedAlertBold from "@icons/RedAlertBold";
import SensorIcon from "@icons/SensorIcon";
import { disabledSubTypes } from "@constants/subTypes";
import { BiError } from "react-icons/bi";
import { MdBatteryUnknown } from "react-icons/md";
import { bacnetUnits } from "../../../../components/AddEditBacnetUiTemplateDialog/constants";
import { formatValue, getOperationalValue, getUniversalMappedPoints } from "./helpers";
import { useBacnetUniversalControls } from "./hooks/useBacnetUniversalControls";
import { useModbusUniversalControls } from "./hooks/useModbusUniversalControls";
import { getActiveSchedulesCount, getUnitActiveSchedulesCount } from "../../../../utils/CommonUtils";
import { useGroupUniversalMembers } from "@hooks/useGroupUniversalMembers";


// ─── Constants ───────────────────────────────────────────────────────────────

/** List of known subtypes, for better code readability, should be updated as new subtypes are added */
const SUBTYPES = {
  "ERV": 2,
}

const hauxHhStyle: React.CSSProperties = {
  fontSize: "9px",
  width: "22px",
  color: "rgb(240, 81, 70)",
  border: "solid 1px",
  padding: "0 1px",
  overflowWrap: "anywhere",
  textAlign: "center",
};

/** Small mode icons — unit mid-lower row. */
const modesIcons: any = {
  0: <ColdMode style={{ transform: "scale(1.389)", fontSize: "17.5px", margin: "0 5px" }} />,
  1: <HotMode style={{ transform: "scale(1.2343)", fontSize: "17.5px", margin: "0 5px" }} />,
  2: <AutoMode style={{ transform: "scale(.76)", fontSize: "17.5px", margin: 0 }} />,
  3: <DryIcon style={{ transform: "scale(0.48)", fontSize: "17.5px", margin: "0 5px", marginLeft: "-7px" }} />,
  4: <Typography component="span" style={{ ...hauxHhStyle }}>HAUX</Typography>,
  5: <ModeFanIcon style={{ transform: "scale(0.6945)", fontSize: "17.5px", margin: "0 5px", marginLeft: "-9px" }} />,
  6: <Typography component="span" style={{ ...hauxHhStyle }}>HH</Typography>,
  7: <AutoVRMode style={{ width: 28, height: 28, fontSize: "17.5px", margin: 0 }} />,
};

/** Large mode icons — unit status circle (when setpoint is not shown). */
const basicModeIcons: any = {
  0: <ColdMode />,
  1: <HotMode />,
  2: <AutoMode />,
  3: <DryIcon style={{ transform: "scale(0.85)" }} />,
  4: <Typography component="span" style={hauxHhStyle}>HAUX</Typography>,
  5: <ModeFanIcon />,
  6: <Typography component="span" style={hauxHhStyle}>HH</Typography>,
  7: <AutoVRMode style={{ width: 34, height: 34 }} />,
};

/** Small mode icons by string specifically for BACnet units based on textual mode. */
const bacnetModesIcons: Record<string, React.ReactNode> = {
  COOL: <ColdMode style={{ transform: "scale(1.389)", fontSize: "17.5px", margin: "0 5px" }} />,
  HEAT: <HotMode style={{ transform: "scale(1.2343)", fontSize: "17.5px", margin: "0 5px" }} />,
  FAN: <ModeFanIcon style={{ transform: "scale(0.6945)", fontSize: "17.5px", margin: "0 5px", marginLeft: "-9px" }} />,
};

/** Large mode icons by string for BACnet unit status circle. */
const bacnetBasicModeIcons: Record<string, React.ReactNode> = {
  COOL: <ColdMode />,
  HEAT: <HotMode />,
  FAN: <ModeFanIcon />,
};

const defaultKeys = {
  modeKey: "activeOperationMode",
  powerKey: "activeOperationStatus",
  ambientTempKey: "ambientTemperature",
  setpointKeyPerMode: {
    0: "activeSetpoint",
    1: "activeSetpoint",
    2: "activeSetpoint",
    3: "activeSetpoint",
    4: "activeSetpoint",
    5: "activeSetpoint",
    6: "activeSetpoint",
    7: "activeSetpoint",
  },
};
const keysBySubType: any = {
  0: defaultKeys,
  1: { modeKey: "heaterMode", powerKey: "heaterTankONOFF", ambientTempKey: "heaterRoomTemp", setpointKeyPerMode: { 0: "heaterCoolingSP", 1: "heaterHeatingSP", 2: "", 3: "", 5: "" } },
  2: defaultKeys,
  3: { modeKey: "activeOperationMode", powerKey: "activeOperationStatus", ambientTempKey: "ambientTemperature", setpointKeyPerMode: { 21: "activeSetpoint", 22: "activeSetpoint", 23: "activeSetpoint", 24: "activeSetpoint" } }
};
const waterHeaterModesToNormalMap: any = { 0: 2, 1: 1, 2: 0 };

// ─── Shared Helpers ──────────────────────────────────────────────────────────

const hasValue = (v: any) => !!v || v === 0;
const stopEvent = (e: any) => { e.stopPropagation(); e.preventDefault(); };

/** Checks if a unit message indicates an error (used by Unit + Group adapters). */
const isMessageError = (message: any): boolean =>
  !!message && `${message}`.replace(/\s/g, "").toUpperCase() !== "OK";

/** Checks device + item connectivity (used by every adapter). */
const checkDisconnected = (devices: any, deviceId: string | undefined, isConnected?: boolean): boolean =>
  !devices[deviceId || ""]?.isConnected || !isConnected;

/** Computes sensor display value — extracted from the dense ternary chain. */
function computeSensorDisplayValue(
  value: number, sensorUnit: string | undefined, sType: number,
  enableMinMaxSelection: boolean, userMax: any, userMin: any,
  valueMax: number, valueMin: number, isCelsius: boolean
): number | string {
  // No unit, or special types — use raw/scaled value
  if (!sensorUnit || sType === 130 || sType === 7) {
    if (enableMinMaxSelection && hasValue(userMax) && hasValue(userMin)) {
      return (((value - valueMin) * (+userMax - +userMin)) / (valueMax - valueMin)) + +userMin;
    }
    return value;
  }

  if (sensorUnit === "Temperature") {
    return (isCelsius || sType === 7) ? value : (value * 9 / 5) + 32;
  }

  if (sensorUnit === "Open/Close") {
    if (sType === 129) return +value === 0 ? t`OFF` : t`ON`;
    return +value === 1 ? t`OPEN` : t`CLOSE`;
  }

  return value;
}

// ═════════════════════════════════════════════════════════════════════════════
//  TYPES
// ═════════════════════════════════════════════════════════════════════════════

interface ItemData {
  name: string;
  isSelected: boolean;
  isDisconnected: boolean;
  hasError?: boolean;
  onClick: () => void;
  extraClass?: string;
  content: React.ReactNode;
}

// ═════════════════════════════════════════════════════════════════════════════
//  SHELL — Flex container. Handles outer row + state classes.
// ═════════════════════════════════════════════════════════════════════════════

interface ShellProps {
  variant?: "item" | "group";
  isSelected: boolean;
  isDisconnected?: boolean;
  hasError?: boolean;
  onClick?: () => void;
  extraClass?: string;
  containerProps?: Record<string, any>;
  children: React.ReactNode;
  expanded?: React.ReactNode;
}

const Shell: React.FC<ShellProps> = memo(({
  variant = "item", isSelected, isDisconnected = false,
  hasError = false, onClick, extraClass, containerProps, children, expanded
}) => {
  const classes = useStyle();
  const isGroup = variant === "group";

  return (
    <Fragment>
      <div
        {...containerProps}
        className={clsx(
          classes.row,
          isGroup ? classes.groupRow : classes.itemRow,
          isSelected && classes.selected,
          (hasError || isDisconnected) && classes.erroredDiv,
          extraClass
        )}
        onClick={onClick}
      >
        {children}
      </div>
      {expanded}
    </Fragment>
  );
});
Shell.displayName = "Shell";

// ═════════════════════════════════════════════════════════════════════════════
//  SHARED MICRO-COMPONENTS — Single source of truth for every repeated pattern.
// ═════════════════════════════════════════════════════════════════════════════

// ─── Name ────────────────────────────────────────────────────────────────────

/** Item name — used by every adapter. Single source for name typography. */
const ItemName: React.FC<{ name: string; bold?: boolean; className?: string; title?: string }> = memo(({ name, bold, className, title }) => {
  const classes = useStyle();
  return (
    <Typography noWrap className={clsx(classes.nameText, bold && classes.boldText, className)} title={title}>
      {name}
    </Typography>
  );
});
ItemName.displayName = "ItemName";

// ─── Layout: MidSection ──────────────────────────────────────────────────────

/** Two-row content area: name on top, secondary items on bottom.
 *  Unit (row=true):  [name] ... [middle] ... [icons]  on one line.
 *  Sensor (row=false): always stacked column. */
const MidSection: React.FC<{
  name: string;
  bold?: boolean;
  nameClassName?: string;
  row?: boolean;
  middle?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ name, bold, nameClassName, row, middle, children }) => {
  const classes = useStyle();
  return (
    <div className={clsx(classes.midSection, row && classes.midSectionRow)}>
      <div className={row ? classes.midUpper : undefined}>
        <ItemName title={name} name={name} bold={bold} className={nameClassName} />
      </div>
      {row ? (
        <div className={classes.midBottomSection}>
          {middle}
          {children && <div className={classes.midLower}>{children}</div>}
        </div>
      ) : (
        <>
          {middle}
          {children && <div className={classes.sensorBottomRow}>{children}</div>}
        </>
      )}
    </div>
  );
};

/** Mid-lower badge — wraps a midLowerItem with optional visibility toggle. */
const Badge: React.FC<{
  visible?: boolean;
  className?: string;
  children: React.ReactNode;
}> = memo(({ visible = true, className, children }) => {
  const classes = useStyle();
  if (!visible) return null;
  return <div className={clsx(classes.midLowerItem, className)}>{children}</div>;
});
Badge.displayName = "Badge";

// ─── Controls ────────────────────────────────────────────────────────────────

/** Reusable toggle — wraps GreenSwitch with shared boilerplate. */
const ItemSwitch: React.FC<{
  checked: boolean;
  disabled?: boolean;
  tooltip?: string;
  onChange: (e: any) => void;
}> = memo(({ checked, disabled, tooltip = "", onChange }) => {
  const theme: any = useTheme();
  return (
    <Tooltip title={tooltip}>
      <span style={{ display: "inline-flex", alignItems: "center" }} onClick={stopEvent}>
        <GreenSwitch
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          color={theme.palette.colors?.switchPower?.colorChecked || theme.palette.colors?.switch?.colorChecked}
          uncheckedColor={theme.palette.colors?.switchPower?.color || theme.palette.colors?.switch?.color}
        />
      </span>
    </Tooltip>
  );
});
ItemSwitch.displayName = "ItemSwitch";

// ─── Status Badges ───────────────────────────────────────────────────────────

/** Unit status circle content — setpoint with half-degree support, or mode icon, or OFF. */
const getUnitStatusContent = (
  isItemOn: boolean,
  showSetpoint: boolean,
  setpoint: number | undefined,
  tempUnit: string,
  mode: number,
  isHalfCDegreeEnabled: boolean,
  isCelsius: boolean
): React.ReactNode => {
  if (!isItemOn) {
    return t`OFF`;
  }
  if (showSetpoint && isNumber(setpoint)) {
    return `${isHalfCDegreeEnabled && isCelsius ? Math.floor(setpoint * 2) / 2 : Math.round(setpoint)}${tempUnit}`;
  }
  return basicModeIcons[mode];
};

/** Circular status badge — shows setpoint/mode/OFF/disconnect/error. */
const StatusCircle: React.FC<{
  isDisconnected: boolean;
  hasError: boolean;
  isOn: boolean;
  isHeat?: boolean;
  isCool?: boolean;
  errorMessage?: string;
  /** Pre-computed content (used when unitContent is not provided). */
  content?: React.ReactNode;
  /** When provided, content is derived from unit logic: setpoint or BasicModeIcons[mode] or OFF. */
  unitContent?: {
    showSetpoint: boolean;
    setpoint: number | undefined;
    tempUnit: string;
    mode: number;
    isHalfCDegreeEnabled: boolean;
    isCelsius: boolean;
  };
}> = memo(({ isDisconnected, hasError, isOn, isHeat, isCool, errorMessage, content, unitContent }) => {
  const classes = useStyle();
  const isHashed = isDisconnected || hasError;
  const resolvedContent =
    unitContent
      ? getUnitStatusContent(
        isOn,
        unitContent.showSetpoint,
        unitContent.setpoint,
        unitContent.tempUnit,
        unitContent.mode,
        unitContent.isHalfCDegreeEnabled,
        unitContent.isCelsius
      )
      : content;
  const contentLength = typeof resolvedContent === "string" ? resolvedContent.length : 0;
  return (
    <div
      className={clsx(
        classes.statusBox,
        isHashed && classes.erroredStatusBox,
        !isHashed && isOn && classes.statusPowerOn,
        !isHashed && isHeat && classes.statusHeat,
        !isHashed && isCool && classes.statusCool,
        contentLength >= 6 ? classes.statusBoxTextXs : contentLength === 5 && classes.statusBoxTextSm
      )}
    >
      {isDisconnected ? (
        <DisconnectIcon className={classes.disconnectedIcon} />
      ) : hasError ? (
        <Typography className={classes.errorMsg}>{errorMessage}</Typography>
      ) : (
        resolvedContent
      )}
    </div>
  );
});
StatusCircle.displayName = "StatusCircle";

/** Square icon box — sensor badge on the left. */
const IconBox: React.FC<{ isDisconnected: boolean; children: React.ReactNode }> = memo(({ isDisconnected, children }) => {
  const classes = useStyle();
  return (
    <div className={classes.iconBox}>
      {isDisconnected ? <DisconnectIcon className={classes.disconnectedIcon} /> : children}
    </div>
  );
});
IconBox.displayName = "IconBox";

/** Disconnected container — shared by PowerMeter and BACnet. */
const DisconnectedBadge: React.FC = memo(() => {
  const classes = useStyle();
  return (
    <div className={classes.disconnectedContainer}>
      <DisconnectIcon className={classes.disconnectedIcon} />
    </div>
  );
});
DisconnectedBadge.displayName = "DisconnectedBadge";

// ─── Indicators ──────────────────────────────────────────────────────────────

/** Schedule icon — shared by all types. Accepts className override for group variant. */
const ScheduleIndicator: React.FC<{ count: number; className?: string }> = memo(({ count, className }) => {
  const classes = useStyle();
  if (count <= 0) return null;
  return <AccessTime className={className || classes.noteIcon} />;
});
ScheduleIndicator.displayName = "ScheduleIndicator";

/** Error alert — RedAlertBold icon with optional message text. */
const ErrorIndicator: React.FC<{ message?: string }> = memo(({ message }) => {
  const classes = useStyle();
  if (!message) {
    return <RedAlertBold width={22} height={22} />;
  }
  return (
    <div className={classes.errorContainer}>
      {message} <RedAlertBold width={22} height={22} style={{ marginLeft: 9 }} />
    </div>
  );
});
ErrorIndicator.displayName = "ErrorIndicator";

/** Info line — icon + label:value pair (group sensor info section). */
const InfoLine: React.FC<{ icon: React.ReactNode; label: string; value: any }> = memo(({ icon, label, value }) => {
  const classes = useStyle();
  return (
    <div className={classes.sensorGroupInfoLine}>
      {icon}
      <Typography>{`${label} ${value}`}</Typography>
    </div>
  );
});
InfoLine.displayName = "InfoLine";

/** Units on/off count display (group bottom row). */
const UnitsCount: React.FC<{ on: number; total: number }> = memo(({ on, total }) => {
  const classes = useStyle();
  return (
    <Typography className={classes.groupInfo}>
      {`${on}/${total} units`}
    </Typography>
  );
});
UnitsCount.displayName = "UnitsCount";

// ─── Shared right-section pattern (error + switch) ──────────────────────────

/** Alert + Switch section — shared by Unit, Sensor, and Group adapters. */
const RightSection: React.FC<{
  show?: boolean;
  errorContent?: React.ReactNode;
  switchProps?: {
    checked: boolean;
    disabled: boolean;
    tooltip?: string;
    onChange: (e: any) => void;
  };
   totalDisconnectedCount?: number;
   schedulesCount?: number;
}> = ({ show = true, errorContent, switchProps, totalDisconnectedCount = 0 ,  schedulesCount = 0 }) => {
  const classes = useStyle();
  return (
    <div className={classes.rightSection}>
      {!!totalDisconnectedCount && (
        <Typography className={classes.disconnectedInfo}>
          <DisconnectIcon viewBox="0 3.43 24 17.14" width={31} height={22} />
        </Typography>
      )}
      {errorContent && (
        <div className={classes.importantNoteSec}>{errorContent}</div>
      )}
      <ScheduleIndicator
        count={schedulesCount}
        className={classes.scheduleIcon}
      />
      {show && switchProps && <ItemSwitch {...switchProps} />}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
//  createItemAdapter — Factory for non-group item types.
//
//  Handles: useStyle(), Shell rendering, null guard, disconnect click guard.
// ═════════════════════════════════════════════════════════════════════════════

function createItemAdapter(
  displayName: string,
  useData: (props: any, classes: any) => ItemData | null
): React.FC<any> {
  const Adapter: React.FC<any> = (props) => {
    const classes = useStyle();
    const data = useData(props, classes);

    if (!data) {
      return null;
    }

    return (
      <Shell
        isSelected={data.isSelected}
        isDisconnected={data.isDisconnected}
        hasError={data.hasError}
        onClick={data.isDisconnected ? undefined : data.onClick}
        extraClass={data.extraClass}
      >
        {data.content}
      </Shell>
    );
  };
  Adapter.displayName = displayName;
  return Adapter;
}

// ═════════════════════════════════════════════════════════════════════════════
//  ITEM ADAPTERS — Only data + content composition using shared components.
// ═════════════════════════════════════════════════════════════════════════════

// ─── Unit ────────────────────────────────────────────────────────────────────

const UnitAdapter = createItemAdapter("UnitAdapter", function useUnitData(
  { unitId, siteId, selectItem, groupId, selectedItem, addMessage, allSchedules, allGroups }: any,
  classes: any
) {
  const types = useStoreState((s) => s.types);
  const devices = useStoreState((s) => s.devices.allDevices);
  const { temperatureScaleMirror } = useStoreState((s) => s);
  const { temperatureScale = 0 } = useStoreState((s) => s.users.me);
  const { showClearFilterAlerts } = useStoreState((s) => s.users.userPreferences);
  const operationStatusesMirror = useStoreState((s) => s.operationStatusesMirror);
  const { updateWaterHeaterDHWTankSwitch, updateUnitLocally } = useStoreActions((s) => s.units);
  const unitPowerControl = useStoreActions((s) => s.units.changePowerState);
  const unit: any = useStoreState((s) => s.units.allUnits[unitId]);

  if (!unit) return null;

  const { operationStatuses, operationModesExtended } = types;
  const { on: powerOnVal, off: powerOffVal } = operationStatusesMirror;
  const tempUnit = types.temperatureScale[temperatureScale as number] === "celsius" ? "°C" : "°F";
  const isCelsius = +temperatureScaleMirror.celsius === +temperatureScale;

  const {
    message, filter, name, isConnected = true, device,
    enableOnoff, enableOnState, isHalfCDegreeEnabled,
    capabilityFlags = {}, subType = 0, setbackActive = false,
    permissions = {}, realActiveOperationMode, hasActiveTimer = false,
  } = unit;

  const activeSchedulesCount = getUnitActiveSchedulesCount(unit, { allSchedules, allGroups });

  const { canControlOperationStatus } = permissions;
  const { enableOperationStatusSelection = false, enableOperationModeSelection = false } = capabilityFlags;
  const { modeKey, powerKey, ambientTempKey, setpointKeyPerMode } = keysBySubType[subType] || defaultKeys;
  const mappedMode = unit[modeKey];
  const mode = subType === 1 ? waterHeaterModesToNormalMap[mappedMode] : mappedMode;
  const setpoint = unit[setpointKeyPerMode[mode]];
  const ambientTemperature = subType !== SUBTYPES.ERV ? unit[ambientTempKey] : undefined;
  const power = unit[powerKey];
  const hasError = isMessageError(message);
  const isDisconnected = checkDisconnected(devices, device, isConnected);
  const isItemOn = +power === +powerOnVal && !isDisconnected;
  const selected = !selectedItem.isGroup && selectedItem.unitId === unitId && selectedItem.groupId === groupId;

  // Mode-specific status styles
  const modeText = operationModesExtended[mode];
  const realModeText = operationModesExtended[realActiveOperationMode];
  const isHeatOn = isItemOn && (modeText === "HEAT" || (modeText === "Virtual Auto" && realModeText === "HEAT"));
  const isCoolOn = isItemOn && (modeText === "COOL" || (modeText === "Virtual Auto" && realModeText === "COOL"));
  const isDryOn = isItemOn && (modeText === "DRY" || (modeText === "Virtual Auto" && realModeText === "DRY"));
  const isHAUXOn = isItemOn && (modeText === "HAUX" || (modeText === "Virtual Auto" && realModeText === "HAUX"));
  const isHHOn = isItemOn && (modeText === "HH" || (modeText === "Virtual Auto" && realModeText === "HH"));
  const isStatusHeat = !isDisconnected && !hasError && (isHeatOn || isHAUXOn || isHHOn);
  const isStatusCool = !isDisconnected && !hasError && (isCoolOn || isDryOn);
  const showSetpoint = !isDisconnected && !hasError && (isHeatOn || isCoolOn || isHAUXOn || isHHOn);

  const togglePower = (event: any) => {
    stopEvent(event);
    const value: number = operationStatuses[power] === "on" ? +powerOffVal : +powerOnVal;
    if (subType === 1) {
      updateWaterHeaterDHWTankSwitch({ id: unitId, data: { operationStatus: value } })
        .then(() => updateUnitLocally({ id: unitId, unit: { heaterTankONOFF: value } }))
        .catch((err: any) => addMessage?.({ message: err.message }));
      return;
    }
    unitPowerControl({ unitId, state: value })
      .catch((err: any) => addMessage?.({ message: err.message }));
  };

  // Error / filter content for right section
  const errorContent = (hasError || filter) ? (
    <>
      {hasError && <RedAlertBold width={22} height={22} />}
      {!hasError && filter && showClearFilterAlerts && (
        <AlertControl color="#f7b500" className={classes.alertIcon} />
      )}
    </>
  ) : undefined;

  return {
    name,
    isSelected: selected,
    isDisconnected,
    hasError,
    onClick: () => selectItem(siteId, { isGroup: false, unitId, groupId, type: "unit" }),

    content: (
      <>
        <StatusCircle
          isDisconnected={isDisconnected}
          hasError={hasError}
          isOn={isItemOn}
          isHeat={isStatusHeat}
          isCool={isStatusCool}
          errorMessage={message}
          unitContent={{
            showSetpoint,
            setpoint,
            tempUnit,
            mode,
            isHalfCDegreeEnabled: !!isHalfCDegreeEnabled,
            isCelsius,
          }}
        />

        <MidSection
          name={name}
          row
          middle={
            !isDisconnected && <div style={{ visibility: subType !== SUBTYPES.ERV ? "visible" : "hidden" }} className={classes.currTemp}>
              <span><b>{t`Now:`}</b> </span>
              <span>{!isNaN(ambientTemperature) ? `${Math.round(ambientTemperature)}${tempUnit}` : "--"}</span>
            </div>
          }
        >
          {!isDisconnected && <Badge visible={enableOperationModeSelection}>{modesIcons[mode] || ""}</Badge>}
          <Badge visible={setbackActive}>SB</Badge>
          <Badge visible={activeSchedulesCount > 0}><ScheduleIndicator count={activeSchedulesCount} /></Badge>
          <Badge visible={hasActiveTimer}>{isItemOn ? <TimerActive /> : <Timer />}</Badge>
        </MidSection>

        <RightSection
          show={!isDisconnected}
          errorContent={errorContent}
          switchProps={enableOperationStatusSelection ? {
            checked: isItemOn,
            disabled: !canControlOperationStatus || enableOnoff || (power === 2 && enableOnState) || disabledSubTypes.includes(unit.subType),
            tooltip: enableOnoff || (power === 2 && enableOnState) ? t`Unit is restricted , Please check with the administrator` : "",
            onChange: (e: any) => canControlOperationStatus && togglePower(e),
          } : undefined}
        />
      </>
    ),
  };
});

// ─── Sensor ──────────────────────────────────────────────────────────────────

const SensorAdapter = createItemAdapter("SensorAdapter", function useSensorData(
  { sensorId, siteId, selectItem, groupId, selectedItem, isCelsius: propIsCelsius, allSchedules }: any,
  classes: any
) {
  const types = useStoreState((s) => s.types);
  const devices = useStoreState((s) => s.devices.allDevices);
  const allSensors = useStoreState((s) => s.sensors.allSensors);
  const { updateSensorValue, _storeUpdateSensor } = useStoreActions((a) => a.sensors);
  const { addMessage } = useStoreActions((a) => a.errorMessage);

  const { sensorTypes, sensorMeasurementUnits } = types;
  const item = allSensors[sensorId] || {};
  const { name, type: sType, readingValue = 0, userData = {}, id, device, schedules = [], canControl = true, isConnected } = item;
  const activeSchedulesCount = getActiveSchedulesCount(schedules, allSchedules);

  const isDisconnected = checkDisconnected(devices, device, isConnected);

  const sensorTypeObj = sensorTypes[sType] || {};
  const { enableMinMaxSelection, measurementUnits, enableMeasurementUnitSelection, valueMax, valueMin } = sensorTypeObj;
  const activeMU = enableMeasurementUnitSelection && userData?.measurementUnitsType ? userData.measurementUnitsType : measurementUnits;
  const sensorUnit = sensorMeasurementUnits[activeMU]?.name;
  const { rangeMax: userMax, rangeMin: userMin } = userData;
  const value = +readingValue;

  const displayValue = computeSensorDisplayValue(
    value, sensorUnit, sType, enableMinMaxSelection, userMax, userMin, valueMax, valueMin, propIsCelsius
  );
  const roundedValue = isNumber(displayValue) ? (Math.round(displayValue * 100) / 100) : displayValue;
  const selected = !selectedItem.isGroup && selectedItem.sensorId === sensorId && selectedItem.groupId === groupId;

  const handleOnOff = (event: any) => {
    stopEvent(event);
    const next = +value === 0 ? 1 : 0;
    updateSensorValue({ id, data: { value: next } })
      .then(() => _storeUpdateSensor({ id, data: { readingValue: next } }))
      .catch((err: any) => addMessage({ message: err.message }));
  };

  return {
    name,
    isSelected: selected,
    isDisconnected,
    onClick: () => selectItem(siteId, { isGroup: false, sensorId, groupId, type: "sensor" }),

    content: (
      <>
        <IconBox isDisconnected={isDisconnected}>
          <SensorIcon smallIcon type={sType} width={25} height={25} readingValue={+readingValue} />
        </IconBox>

        <MidSection name={name}>
          <Typography className={classes.sensorValue}>
            {t`Now`}: {roundedValue}
            <span className={clsx({ [classes.specialFont]: [2, 3, 6, 14].includes(activeMU) })}>
              {symbols[activeMU || ""] || ""}
            </span>
          </Typography>
          <ScheduleIndicator count={activeSchedulesCount} />
        </MidSection>

        <RightSection
          show={sType === 129 && !isDisconnected}
          switchProps={{
            checked: value === 1,
            disabled: !canControl,
            onChange: handleOnOff,
          }}
        />
      </>
    ),
  };
});

// ─── PowerMeter ──────────────────────────────────────────────────────────────

const PowerMeterAdapter = createItemAdapter("PowerMeterAdapter", function usePowerMeterData(
  { id, siteId, selectItem, groupId, selectedItem }: any,
  classes: any
) {
  const allPowerMeters = useStoreState((s) => s.powerMeters.allPowerMeters);
  const devices = useStoreState((s) => s.devices.allDevices);
  const item = allPowerMeters[id];

  if (!item) return null;

  const { name = "", power = 0, device = "" } = item;
  const selected = selectedItem.id === id && selectedItem.groupId === groupId;
  const isDisconnected = checkDisconnected(devices, device, item.isConnected);

  return {
    name,
    isSelected: selected,
    isDisconnected,
    onClick: () => selectItem(siteId, { isGroup: false, id, groupId, type: "powerMeter" }),

    content: (
      <>
        <div className={classes.nameSection}>
          <ItemName name={name} className={classes.nameTextLg} />
        </div>
        {!isDisconnected ? (
          <Typography className={classes.powerValue}>
            {Math.round(power * 100) / 100}
            <span className={classes.powerUnit}>kW</span>
          </Typography>
        ) : (
          <DisconnectedBadge />
        )}
      </>
    ),
  };
});

// ─── BacnetUnit ──────────────────────────────────────────────────────────────

const BacnetAdapter = createItemAdapter("BacnetAdapter", function useBacnetData(
  { entry, siteId, selectItem, groupId, selectedItem, siteUiTemplates, siteParamTemplates, bacnetTemplatesLoading, allSchedules }: any,
  classes: any
) {
  const devices = useStoreState((s) => s.devices.allDevices);
  const { title = "", id, isConnected, device, schedules = [] } = entry;
  const activeSchedulesCount = getActiveSchedulesCount(schedules, allSchedules);

  const bacnet = useBacnetUniversalControls(entry, siteUiTemplates, siteParamTemplates);
  const isSelectable = true;
  const isDisconnected = checkDisconnected(devices, device, isConnected);
  const displayName = title || id;

  const shouldShowUniversalControls = bacnet.isReady && !isDisconnected;
  const isLoadingUniversalControls = (!!bacnetTemplatesLoading || bacnet.isLoading) && !bacnet.isReady;

  if (isLoadingUniversalControls) {
    return null;
  }



  const isValidUniversalUnit = bacnet.isUniversalUnit && bacnet.hasUniversalControls;
  const hasUiOrValidUniversalUnit = !!entry.uiTemplate || isValidUniversalUnit;

  if (bacnet.isUniversalUnit && !hasUiOrValidUniversalUnit) {
    return null;
  }

  const selectedMatch = selectedItem.id === id && selectedItem.groupId === groupId;
  const extraClass = clsx(!isSelectable && classes.dimmedItem);
  const onClick = () => {
    selectItem(siteId, { isGroup: false, id, groupId, type: "bacnetUnit" });
  };

  if (shouldShowUniversalControls) {
    const showCircle = !!(bacnet.onOffKey || bacnet.setpointKey);
    const hasRoomTemp = !!bacnet.roomTempKey && typeof bacnet.currentRoomTemp === "number";
    const statusContent =
      bacnet.showSetpoint && typeof bacnet.currentSetpoint === "number"
        ? `${Math.round(bacnet.currentSetpoint)}${bacnet.tempUnit}`
        : bacnet.isFanOn && bacnet.setpointKey
          ? bacnetBasicModeIcons['FAN']
          : bacnet.isOn
            ? t`ON`
            : t`OFF`;

    return {
      name: displayName,
      isSelected: selectedMatch,
      isDisconnected,
      extraClass,
      onClick,
      content: (
        <>
          {showCircle &&
            <StatusCircle
              isDisconnected={isDisconnected}
              hasError={false}
              isOn={bacnet.isOn}
              isHeat={bacnet.isStatusHeat}
              isCool={bacnet.isStatusCool}
              content={statusContent}
            />
          }
          <MidSection
            name={displayName}
            row
            middle={
              <>
                {!showCircle && <div className={classes.emptySection}></div>}
                {(showCircle || hasRoomTemp) && (
                  <div className={classes.currTemp} style={hasRoomTemp ? undefined : { visibility: "hidden" }}>
                    <span><b>{t`Now:`}</b> </span>
                    <span>{hasRoomTemp ? `${Math.round(bacnet.currentRoomTemp)}${bacnet.tempUnit}` : ""}</span>
                  </div>
                )}
              </>
            }
          >
            <Badge visible={!!bacnet.modeText}>{bacnetModesIcons[bacnet.modeText] || ""}</Badge>
            <Badge visible={activeSchedulesCount > 0}><ScheduleIndicator count={activeSchedulesCount} /></Badge>
          </MidSection>
          <RightSection
            show={!isDisconnected}
            switchProps={bacnet.onOffKey ? {
              checked: bacnet.isOn,
              disabled: false,
              tooltip: "",
              onChange: bacnet.handleOnOffToggle,
            } : undefined}
          />
        </>
      ),
    };
  }

  // Default bacnet unit rendering (without universal controls)
  return {
    name: displayName,
    isSelected: selectedMatch,
    isDisconnected,
    extraClass,
    onClick,
    content: (
      <>
        <div className={classes.nameSection}>
          <ItemName name={displayName} />
        </div>
        <div className={classes.importantNoteSec}>
          <ScheduleIndicator count={activeSchedulesCount} />
        </div>
        {isDisconnected && <DisconnectedBadge />}
      </>
    ),
  };
});

// ═════════════════════════════════════════════════════════════════════════════
//  MODBUS ADAPTER
// ═════════════════════════════════════════════════════════════════════════════

const ModbusAdapter = createItemAdapter("ModbusAdapter", function useModbusData(
  { entry, siteId, selectItem, groupId, selectedItem }: any,
  classes: any
) {
  const { id, name, isConnected } = entry;
  // Computed server-side from the same schedule documents used to build
  // `schedules`, so no need to cross-reference the customer-wide fetch.
  const activeSchedulesCount = entry.activeSchedulesCount ?? 0;
  const isDisconnected = !isConnected;
  const selectedMatch = selectedItem.id === id && selectedItem.groupId === groupId;

  const modbus = useModbusUniversalControls(entry);
  const shouldShowUniversalControls = modbus.isReady && !isDisconnected;

  const onClick = () => selectItem(siteId, { isGroup: false, id, groupId, type: "modbusUnit" });

  if (shouldShowUniversalControls) {
    const showCircle = !!(modbus.onOffKey || modbus.setpointKey);
    const hasRoomTemp = !!modbus.roomTempKey && typeof modbus.currentRoomTemp === "number";
    const statusContent =
      modbus.showSetpoint && typeof modbus.currentSetpoint === "number"
        ? `${Math.round(modbus.currentSetpoint)}${modbus.tempUnit}`
        : modbus.isFanOn && modbus.setpointKey
          ? bacnetBasicModeIcons["FAN"]
          : modbus.isOn
            ? t`ON`
            : t`OFF`;

    return {
      name,
      isSelected: selectedMatch,
      isDisconnected,
      onClick,
      content: (
        <>
          {showCircle && (
            <StatusCircle
              isDisconnected={isDisconnected}
              hasError={false}
              isOn={modbus.isOn}
              isHeat={modbus.isStatusHeat}
              isCool={modbus.isStatusCool}
              content={statusContent}
            />
          )}
          <MidSection
            name={name}
            row
            middle={
              <>
                {!showCircle && <div className={classes.emptySection}></div>}
                {(showCircle || hasRoomTemp) && (
                  <div className={classes.currTemp} style={hasRoomTemp ? undefined : { visibility: "hidden" }}>
                    <span><b>{t`Now:`}</b> </span>
                    <span>{hasRoomTemp ? `${Math.round(modbus.currentRoomTemp)}${modbus.tempUnit}` : ""}</span>
                  </div>
                )}
              </>
            }
          >
            <Badge visible={!!modbus.modeText}>{bacnetModesIcons[modbus.modeText] || ""}</Badge>
          </MidSection>
          {/* Keep the icon as a flat sibling, matching the default branch,
              so its position stays consistent across both branches. */}
          <div className={classes.importantNoteSec}>
            <ScheduleIndicator count={activeSchedulesCount} />
          </div>
          {/* Render only when there's a toggle to show; an empty wrapper would
              shift the schedule icon left compared to the default branch. */}
          {modbus.onOffKey && (
            <RightSection
              show={!isDisconnected}
              switchProps={{
                checked: modbus.isOn,
                disabled: false,
                tooltip: "",
                onChange: modbus.handleOnOffToggle,
              }}
            />
          )}
        </>
      ),
    };
  }

  return {
    name,
    isSelected: selectedMatch,
    isDisconnected,
    onClick,
    content: (
      <>
        <div className={classes.nameSection}>
          <ItemName name={name} />
        </div>
        <div className={classes.importantNoteSec}>
          <ScheduleIndicator count={activeSchedulesCount} />
        </div>
        {isDisconnected && <DisconnectedBadge />}
      </>
    ),
  };
});

// ═════════════════════════════════════════════════════════════════════════════
//  GROUP ADAPTER
// ═════════════════════════════════════════════════════════════════════════════

const GroupAdapter: React.FC<any> = (props) => {
  const {
    canControlSiteOperationStatus = true, openCollapse = true, addMessage,
    disableDragging, handleDragging, item, siteId, selectItem,
    selectedItem, changeSitePower, onDragEnd, isCelsius,
    siteUiTemplates, siteParamTemplates, bacnetTemplatesLoading, allSchedules, allGroups,
  } = props;

  const {
    id: itemId, isSensorGroup, name,
    units = [], sensors = [], schedules = [],
    powerMeters = [], itemsOrder = [],
    permissions = {}, bacnetUnits = {}, modbusUnits = {},
  } = item;
  const activeSchedulesCount = getActiveSchedulesCount(schedules, allSchedules);
  const { canControlOperationStatus } = permissions;

  const classes = useStyle();
  const theme: any = useTheme();
  const [open, setOpen] = useState<boolean>((selectedItem?.firstTimeLoading && selectedItem.groupId === itemId) || false);
  const operationStatusesMirror = useStoreState((s) => s.operationStatusesMirror);
  const groupPowerControl = useStoreActions((s) => s.groups.changePowerState);
  const allUnits = useStoreState((s) => s.units.allUnits);
  const setUnitsActiveOperationStatus = useStoreActions((s) => s.units.setUnitsActiveOperationStatus);
  const devices = useStoreState((s) => s.devices.allDevices);
  const allSensors = useStoreState((s) => s.sensors.allSensors);
  const allSensorGroups = useStoreState((s) => s.sensors.allSensorGroups);
  const allPowerMeters = useStoreState((s) => s.powerMeters.allPowerMeters);
  const getUnitsBy = useStoreState((s) => s.units.getUnitsBy);

  const { on: powerOnVal } = operationStatusesMirror;
  const [disablePowerBtn, setDisablePowerBtn] = useState<boolean>(false);

  // BACnet/Modbus members only take part in group power when their on/off point is mapped
  const isUserGroup = !isSensorGroup && !["allUnits", "sensors", "bacnetUnits", "modbusUnits", "powerMeters"].includes(itemId);
  const { unitShapes: universalShapes } = useGroupUniversalMembers({ group: isUserGroup ? item : null, siteId, live: true });
  const powerControlledUniversalShapes = useMemo(
    () => Object.values(universalShapes).filter((shape: any) => shape.capabilityFlags?.enableOperationStatusSelection && shape.isConnected),
    [universalShapes]
  );

  const sensorGroupObj = isSensorGroup && itemId && allSensorGroups[itemId];
  const hasVisibleSensor = sensorGroupObj?.sensors.some((sid: string) => allSensors[sid]?.isVisible);

  const sensorErrorValue = useMemo(() => {
    if (!sensorGroupObj) return null;
    return sensorGroupObj.sensors.reduce((val: any, sid: any) => {
      if (val !== null) return val;
      const s = allSensors[sid];
      return s?.isVisible && +s?.type === 23 ? s.readingValue : null;
    }, null);
  }, [sensorGroupObj, allSensors]);

  const sensorBatteryValue = useMemo(() => {
    if (!sensorGroupObj) return null;
    return sensorGroupObj.sensors.reduce((val: any, sid: any) => {
      if (val !== null) return val;
      const s = allSensors[sid];
      return s?.isVisible && +s?.type === 22 ? s.readingValue : null;
    }, null);
  }, [sensorGroupObj, allSensors]);

  useEffect(() => { if (!openCollapse) setOpen(true); }, [openCollapse]);

  useEffect(() => {
    let connected = false;
    for (const uid of units) {
      const u: any = allUnits[uid];
      if (u?.isConnected && devices[u?.device || ""]?.isConnected) { connected = true; break; }
    }
    if (!connected && powerControlledUniversalShapes.length) {
      connected = true;
    }
    if (!connected) {
      for (const sid of sensors) {
        const s: any = allSensors[sid];
        if (s?.type === 129 && devices[s?.device || ""]?.isConnected) { connected = true; break; }
      }
    }
    setDisablePowerBtn(!connected);
  }, [item, powerOnVal, allUnits, devices, allSensors, units, sensors, powerControlledUniversalShapes]);

  const changeGroupPower = useCallback((event: any, powerOn: boolean) => {
    stopEvent(event);
    const state = powerOn ? 1 : 2;
    const siteUnits = getUnitsBy("site", siteId, { type: "control" });
    const connectedUnits = units.filter((uid: any) => {
      const u = allUnits[uid];
      return u && u.isConnected && devices[u.device]?.isConnected;
    });

    itemId === "allUnits"
      ? (siteUnits.length === units?.length)
        ? changeSitePower({ state, siteId })
        : setUnitsActiveOperationStatus({ units: connectedUnits, operationStatus: state })
      : groupPowerControl({ groupId: itemId, state })
        .catch((err: any) => addMessage({ message: err.message }));
  }, [itemId, units, allUnits, devices, siteId, getUnitsBy, changeSitePower, setUnitsActiveOperationStatus, groupPowerControl, addMessage]);

  const onCount = useMemo(() =>
    units.filter((uid: any) => {
      const u = allUnits[uid];
      return u && u.activeOperationStatus === +powerOnVal && u.isConnected && devices[u.device]?.isConnected;
    }).length
    + sensors.filter((sid: any) => {
      const s = allSensors[sid];
      return s?.type === 129 && s?.readingValue === 1 && devices[s?.device]?.isConnected;
    }).length
    + powerControlledUniversalShapes.filter((shape: any) => shape.activeOperationStatus === +powerOnVal).length,
    [units, sensors, allUnits, allSensors, devices, powerOnVal, powerControlledUniversalShapes]
  );

  const isAnyUnitInError = useMemo(() =>
    units?.some((uid: any) => isMessageError(allUnits?.[uid]?.message)),
    [units, allUnits]
  );

  const disconnectedUnitsCount = useMemo(
    () => units.filter((uid: any) => {
      const u = allUnits[uid];
      return u && (!u.isConnected || !devices[u.device]?.isConnected);
    }).length,
    [units, allUnits, devices]
  );

  const disconnectedSensorsCount = useMemo(
    () => sensors.filter((sid: any) => {
      const s = allSensors[sid];
      return s && !devices[s.device]?.isConnected;
    }).length,
    [sensors, allSensors, devices]
  );

  const disconnectedPowerMetersCount = useMemo(
    () => powerMeters.filter(([id]: any) => {
      const pm = allPowerMeters[id];
      return pm && (!pm.isConnected || !devices[pm.device]?.isConnected);
    }).length,
    [powerMeters, allPowerMeters, devices]
  );

  const totalDisconnectedCount = disconnectedUnitsCount + disconnectedSensorsCount + disconnectedPowerMetersCount;

  const showGroupActions = useMemo(
    () => !["bacnetUnits", "powerMeters"].includes(itemId) && !isSensorGroup && itemId !== "sensors" && (units.length > 0 || sensors.length > 0),
    [itemId, isSensorGroup, units.length, sensors.length]
  );
  const showErrorsAndWarnings = useMemo(() => !!sensorGroupObj && !["bacnetUnits"].includes(itemId), [sensorGroupObj, itemId]);

  const isSelected = selectedItem.isGroup && selectedItem.groupId === itemId;

  const toggleOpen = useCallback((e: any) => { e.stopPropagation(); setOpen((prev) => !prev); }, []);
  const handleGroupClick = useCallback(
    () => selectItem(siteId, { isGroup: true, groupId: itemId, unitId: "", type: "" }),
    [selectItem, siteId, itemId]
  );

  if (isSensorGroup && !hasVisibleSensor) return <></>;

  return (
    <Shell
      variant="group"
      isSelected={isSelected}
      onClick={handleGroupClick}
      containerProps={handleDragging}
      expanded={
        <DragDropContext onDragEnd={(result: any) => onDragEnd(result)(itemId)}>
          <Droppable droppableId="list">
            {(provided: any) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {open && itemsOrder.map((entry: any, index: number) => {
                  const [id, show] = entry;
                  if (!show) return null;
                  if (allUnits[id]) return (
                    <Draggable isDragDisabled={disableDragging} key={`g-${itemId}-u-${id}`} draggableId={id} index={index}>
                      {(p: any) => <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}><ControlItem type="unit" addMessage={addMessage} selectedItem={selectedItem} unitId={id} siteId={siteId} selectItem={selectItem} groupId={itemId} allSchedules={allSchedules} allGroups={allGroups} /></div>}
                    </Draggable>
                  );
                  if (allSensors[id]?.isVisible) return (
                    <Draggable isDragDisabled={disableDragging} key={`g-${itemId}-s-${id}`} draggableId={id} index={index}>
                      {(p: any) => <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}><ControlItem type="sensor" selectedItem={selectedItem} sensorId={id} siteId={siteId} selectItem={selectItem} groupId={itemId} isCelsius={isCelsius} allSchedules={allSchedules} /></div>}
                    </Draggable>
                  );
                  if (bacnetUnits[id]) return (
                    <Draggable isDragDisabled={disableDragging} key={`g-${itemId}-b-${id}`} draggableId={id} index={index}>
                      {(p: any) => <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}><ControlItem type="bacnetUnit" entry={bacnetUnits[id]} selectedItem={selectedItem} siteId={siteId} groupId={itemId} selectItem={selectItem} siteUiTemplates={siteUiTemplates} siteParamTemplates={siteParamTemplates} bacnetTemplatesLoading={bacnetTemplatesLoading} allSchedules={allSchedules} /></div>}
                    </Draggable>
                  );
                  if (modbusUnits[id]) return (
                    <Draggable isDragDisabled={disableDragging} key={`g-${itemId}-m-${id}`} draggableId={id} index={index}>
                      {(p: any) => <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}><ControlItem type="modbusUnit" entry={modbusUnits[id]} selectedItem={selectedItem} siteId={siteId} groupId={itemId} selectItem={selectItem} allSchedules={allSchedules} /></div>}
                    </Draggable>
                  );
                  return null;
                })}
                {open && !isEmpty(powerMeters) && powerMeters.map((pm: any) =>
                  !pm[1] ? null : <ControlItem type="powerMeter" key={`g-${itemId}-p-${pm[0]}`} id={pm[0]} selectedItem={selectedItem} siteId={siteId} selectItem={selectItem} groupId={itemId} />
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      }
    >
      <IconButton
        disableFocusRipple
        disableRipple
        onClick={toggleOpen}
        className={classes.iconHolder}
      >
        <ArrowDownO
          style={{ transform: open ? "unset" : "rotate(270deg)" }}
          fill={theme.palette.background.controlArrowDown || null}
        />
      </IconButton>

      <div className={classes.innerContainer}>
        <div className={classes.leftSection}>
          <div className={classes.topRow}>
            <ItemName title={name} name={name} bold={isSelected} className={clsx(classes.nameTextLg, classes.groupName)} />
          </div>
          <div className={classes.bottomRow}>
            {itemId !== "bacnetUnits" && showGroupActions && (
              <UnitsCount on={onCount} total={units.length + sensors.length} />
            )}
          </div>
        </div>

        {showErrorsAndWarnings && (
          <div className={classes.sensorGroupInfoSection}>
            {sensorErrorValue !== null && <InfoLine icon={<BiError style={{ fontSize: 18 }} />} label={t`Error:`} value={sensorErrorValue} />}
            {sensorBatteryValue !== null && <InfoLine icon={<MdBatteryUnknown style={{ fontSize: 18 }} />} label={t`Battery:`} value={sensorBatteryValue} />}
          </div>
        )}

        <RightSection
          totalDisconnectedCount={totalDisconnectedCount}
          schedulesCount={activeSchedulesCount}
          show={showGroupActions}
          errorContent={itemId !== "allUnits" && isAnyUnitInError ? <ErrorIndicator /> : undefined}
          switchProps={{
            checked: onCount > 0,
            disabled: disablePowerBtn || (itemId === "allUnits" ? !canControlSiteOperationStatus : !canControlOperationStatus),
            onChange: (e: any) => changeGroupPower(e, !(onCount > 0)),
          }}
        />
      </div>
    </Shell>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
//  REGISTRY + MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════

const ITEM_COMPONENTS: Record<string, React.FC<any>> = {
  unit: UnitAdapter,
  sensor: SensorAdapter,
  powerMeter: PowerMeterAdapter,
  bacnetUnit: BacnetAdapter,
  modbusUnit: ModbusAdapter,
  group: GroupAdapter,
};

interface ControlItemProps {
  type: "unit" | "sensor" | "powerMeter" | "bacnetUnit" | "modbusUnit" | "group";
  [key: string]: any;
}

const ControlItem: React.FC<ControlItemProps> = ({ type, ...rest }) => {
  const Component = ITEM_COMPONENTS[type];
  if (!Component) {
    return null;
  }
  return <Component {...rest} />;
};

export default ControlItem;
